import { css } from 'aphrodite';
import { format, isSameYear } from 'date-fns';
import React from 'react';
import { AIConversation } from '../../../../extViewmodels/sdk/models/AI';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MessageCircleIcon } from '../../../components/svgs/icons/MessageCircleIcon';
import { s } from './styles';
import { useAIStreamConversationState } from '.';

export const UNTITLED_CONVERSATION_LABEL = 'Untitled conversation';

const formatConversationTimestamp = (value?: string) => {
	if (!value) {
		return '';
	}
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) {
		return '';
	}
	return format(date, isSameYear(date, new Date()) ? 'MMM d, h:mm a' : "MMM d, yyyy 'at' h:mm a");
};

function ConversationHistoryRow({
	conversation,
	isSelected,
	onSelect,
}: {
	conversation: AIConversation;
	isSelected: boolean;
	onSelect: (conversationId: string) => void;
}) {
	const state = useAIStreamConversationState(conversation.id);
	const isStreaming = state?.status === 'loading' || state?.status === 'streaming';
	const timestamp = formatConversationTimestamp(conversation.creationDate);
	return (
		<button className={css(s.historyRow, isSelected && s.historyRowSelected)} onClick={() => onSelect(conversation.id)}>
			<span className={css(s.historyRowTitle, !conversation.title && s.historyRowTitleUntitled)}>
				{conversation.title || UNTITLED_CONVERSATION_LABEL}
			</span>
			{timestamp || isStreaming ? (
				<span className={css(s.historyRowMeta)}>
					{isStreaming ? (
						<>
							<span className={css(s.historyStreamingDot)} />
							<span>Responding…</span>
						</>
					) : (
						<span>{timestamp}</span>
					)}
				</span>
			) : null}
		</button>
	);
}

export function ConversationHistoryPanel({
	conversations,
	isLoading,
	selectedConversationId,
	onSelectConversation,
}: {
	conversations?: AIConversation[];
	isLoading?: boolean;
	selectedConversationId?: string;
	onSelectConversation: (conversationId: string) => void;
}) {
	if (isLoading) {
		return (
			<div className={css(s.historyEmpty)}>
				<LoadingSpinner />
			</div>
		);
	}

	if (!conversations?.length) {
		return (
			<div className={css(s.historyEmpty)}>
				<MessageCircleIcon />
				<span>No conversations yet. Ask Lev something to start one.</span>
			</div>
		);
	}

	return (
		<div className={css(s.historyList)}>
			{conversations.map(conversation => (
				<ConversationHistoryRow
					key={conversation.id}
					conversation={conversation}
					isSelected={conversation.id === selectedConversationId}
					onSelect={onSelectConversation}
				/>
			))}
		</div>
	);
}
